import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FaceShape,
  NAMED_FACE_SHAPES,
  type FaceCandidateSummary,
  type FaceShape as FaceShapeValue,
  type MeasurementSessionSummary,
} from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { apiErrorMessage } from '@/services/apiClient';
import { useConfirmFaceShapeMutation } from '../api/face.api';
import { FaceShapeGlyph } from './FaceShapeGlyph';

const CHOICES: FaceShapeValue[] = [...NAMED_FACE_SHAPES, FaceShape.MIXED, FaceShape.UNKNOWN];

/**
 * The desk deciding a face shape, with the camera's suggestion in front of it.
 *
 * The suggestion is shown and highlighted, and it is not selected. Confirming is a separate act from
 * being shown what the machine thought — which is the whole point, because the machine has been
 * checked against synthetic faces and against no real ones. What is saved is what a person picked.
 *
 * No score and no percentage appears here. The reasons do, as whole translated sentences.
 */
export function FaceReviewPanel({
  sessionId,
  candidate,
  onDone,
  onRetake,
}: {
  sessionId: string;
  candidate: FaceCandidateSummary;
  /** Handed the session the confirmation returned, so the caller knows it is finished without asking again. */
  onDone: (session: MeasurementSessionSummary) => void;
  onRetake: () => void;
}) {
  const { t } = useTranslation();
  const confirm = useConfirmFaceShapeMutation();

  const [shape, setShape] = useState<FaceShapeValue | null>(null);
  const [components, setComponents] = useState<FaceShapeValue[]>([]);
  const [error, setError] = useState<string | null>(null);

  const suggested = candidate.suggestedShape;
  const isUnclear = suggested === FaceShape.UNKNOWN;
  const isMixed = shape === FaceShape.MIXED;
  const ready = shape !== null && (!isMixed || components.length === 2);

  const toggleComponent = (choice: FaceShapeValue) =>
    setComponents((current) =>
      current.includes(choice)
        ? current.filter((c) => c !== choice)
        : [...current, choice].slice(-2),
    );

  const submit = () => {
    if (!shape) return;
    setError(null);
    confirm.mutate(
      isMixed
        ? { sessionId, confirmedShape: shape, componentA: components[0], componentB: components[1] }
        : { sessionId, confirmedShape: shape },
      {
        onSuccess: onDone,
        onError: (caught) => setError(apiErrorMessage(caught) ?? t('face.errors.record')),
      },
    );
  };

  return (
    <div className="space-y-4">
      {error ? <Alert tone="error">{error}</Alert> : null}

      {isUnclear ? (
        <Alert tone="warning">{t('face.review.unclear')}</Alert>
      ) : (
        <div className="border-line space-y-3 rounded-lg border bg-white p-4">
          <div>
            <p className="text-muted text-xs font-medium tracking-wide uppercase">
              {t('face.review.closest')}
            </p>
            <p className="text-ink text-2xl font-semibold">
              {suggested === FaceShape.MIXED &&
              candidate.suggestedComponentA &&
              candidate.suggestedComponentB
                ? t('face.components', {
                    a: t(`face.shapes.${candidate.suggestedComponentA}`),
                    b: t(`face.shapes.${candidate.suggestedComponentB}`),
                  })
                : t(`face.shapes.${suggested}`)}
            </p>
          </div>

          {candidate.runnerUpShape ? (
            <div>
              <p className="text-muted text-xs font-medium tracking-wide uppercase">
                {t('face.review.alsoClose')}
              </p>
              <p className="text-ink text-sm">{t(`face.shapes.${candidate.runnerUpShape}`)}</p>
            </div>
          ) : null}

          {candidate.reasonCodes.length > 0 ? (
            <div>
              <p className="text-muted text-xs font-medium tracking-wide uppercase">
                {t('face.review.why')}
              </p>
              {/* Whole translated sentences, one per code. Never assembled from fragments. */}
              <ul className="text-ink mt-1 list-disc space-y-0.5 ps-5 text-sm">
                {candidate.reasonCodes.map((code) => (
                  <li key={code}>{t(`face.reasons.${code}`)}</li>
                ))}
              </ul>
            </div>
          ) : null}

          <p className="text-muted text-xs">{t('face.review.suggestionOnly')}</p>
        </div>
      )}

      <fieldset>
        <legend className="text-muted mb-2 text-xs font-medium tracking-wide uppercase">
          {t('face.review.chooseShape')}
        </legend>
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
          {CHOICES.map((choice) => (
            <ShapeButton
              key={choice}
              shape={choice}
              isSelected={shape === choice}
              isSuggested={choice === suggested}
              onSelect={() => {
                setShape(choice);
                if (choice !== FaceShape.MIXED) setComponents([]);
              }}
            />
          ))}
        </div>
      </fieldset>

      {isMixed ? (
        <fieldset>
          <legend className="text-muted mb-2 text-xs font-medium tracking-wide uppercase">
            {t('face.set.chooseComponents')}
          </legend>
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
            {NAMED_FACE_SHAPES.map((choice) => (
              <ShapeButton
                key={choice}
                shape={choice}
                isSelected={components.includes(choice)}
                onSelect={() => toggleComponent(choice)}
              />
            ))}
          </div>
          <p className="text-muted mt-2 text-xs">{t('face.set.componentsHint')}</p>
        </fieldset>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <Button onClick={submit} disabled={!ready} isLoading={confirm.isPending}>
          {t('face.review.confirm')}
        </Button>
        <Button variant="secondary" onClick={onRetake} disabled={confirm.isPending}>
          {t('face.review.retake')}
        </Button>
      </div>
    </div>
  );
}

function ShapeButton({
  shape,
  isSelected,
  isSuggested,
  onSelect,
}: {
  shape: FaceShapeValue;
  isSelected: boolean;
  isSuggested?: boolean;
  onSelect: () => void;
}) {
  const { t } = useTranslation();

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={isSelected}
      className={`relative flex flex-col items-center gap-1.5 rounded-lg border p-2.5 text-center transition-colors ${
        isSelected
          ? 'border-brand-600 bg-brand-50 text-brand-700'
          : isSuggested
            ? 'border-brand-300 text-ink bg-white'
            : 'border-line text-muted hover:border-brand-300 hover:text-ink bg-white'
      }`}
    >
      <FaceShapeGlyph shape={shape} className="h-12 w-9" />
      <span className="text-xs font-medium">{t(`face.shapes.${shape}`)}</span>
    </button>
  );
}
